/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import {
  REPORT_JOB_DETAILS,
  REPORT_JOB_LENDER_S_SCHEDULE,
  ReportService,
} from '@gh/builder-shared';
import {
  CommunicationRestService,
  CommunicationType,
  ContactRestService,
  ContactType,
  ContractType,
  EDocSigningStatus,
  EDocType,
  InvoiceType,
  JobChangeOrderRestService,
  JobCommunicationRestService,
  JobDatesRestService,
  JobDrawsRestService,
  JobEDocRestService,
  JobGeneralInfoRestService,
  JobInsuranceRestService,
  JobLetterDocRestService,
  JobChecklistsRestService,
  JobRestService,
  JobRfqRestService,
  JobSharedDocRestService,
  JobSharedDocType,
  JobStageRestService,
  JobWarrantyRestService,
  LetterRestService,
  reportIdField,
  SalesFolderStatus,
  SystemRoleBit,
  systemRolesBitSet,
  UserAutocompleteDto,
  UserAutocompleteQuery,
  UserRestService,
  UserSystemRole,
  JobChecklistProduceDto,
} from '@gh/builder-shared-data';
import {
  dictionaryItemFromDto,
  DictionaryService,
  getRouteParam,
  globalDictionaries,
  InitViewService,
} from '@gh/common-shared';
import { AutocompleteItem, EntityVersion, ID, PrintEntryPoint } from '@gh/common-shared-data';
import { Enum, FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$, switchMap$ } from '@gh/rx/operators';

import { assign, isNil } from 'lodash';
import { Observable } from 'rxjs';

import {
  CommunicationData,
  CommunicationListItem,
  ContracturalCompletionRequest,
  ContracturalCompletionResponse,
  JobBasicInfo,
  JobDatesSave,
  JobDatesTabInfo,
  JobDrawsDetails,
  jobDrawsDetailsFromDto,
  JobDrawsRecalculateRequest,
  JobEDocLog,
  JobEDocOwnerSet,
  JobEDocSigningRequest,
  jobGeneralInfoToDto,
  JobGeneralTabInfo,
  JobInsurance,
  JobRfq,
  JobSharedDocListItem,
  JobSharedDocSet,
  jobSharedDocSetToDto,
  JobVariationResponse,
  JobWarrantyFilter,
  JobWarrantyTask,
  jobWarrantyTaskFromDto,
  Letter,
  letterListFromDto,
  LetterListItem,
  newEmptyWarrantyTask,
  rfqFromDto,
  UserAssignedRole,
  UserListItem,
  JobChecklistItem,
  JobChecklist,
} from './job-details-tab-model';
import { JobCustomer, JobSalesPerson } from '../job.model';

function getParentJobId(route: ActivatedRouteSnapshot): ID {
  if (route.parent) {
    return route.parent.params['jobId'];
  } else {
    throw new Error('JobDetailsTabBusinessService: route parent is not defined');
  }
}

export interface JobDetailsWarrantyTaskDialogConfig {
  basicInfo: JobBasicInfo;
  taskId?: ID;
}

@Injectable()
export class JobDetailsTabBusinessService implements InitViewService {
  jobId: ID;

  constructor(private generalTabRestService: JobGeneralInfoRestService,
              private jobWarrantyRestService: JobWarrantyRestService,
              private gridLayoutService: GridLayoutService,
              private contactRestService: ContactRestService,
              private dictionaryService: DictionaryService,
              private jobStageRestService: JobStageRestService,
              private jobRest: JobRestService,
              private jobDatesInfoRestService: JobDatesRestService,
              private userRestService: UserRestService,
              private jobRfqRestService: JobRfqRestService,
              private lettersRestService: JobLetterDocRestService,
              private jobChecklistsRestService: JobChecklistsRestService,
              private jobEDocRest: JobEDocRestService,
              private jobSharedDocRest: JobSharedDocRestService,
              private letterRest: LetterRestService,
              private jobChangeOrderRestService: JobChangeOrderRestService,
              private insuranceRestService: JobInsuranceRestService,
              private communicationRestService: JobCommunicationRestService,
              private communicationService: CommunicationRestService,
              private jobDrawsRest: JobDrawsRestService,
              private report: ReportService) {
  }

  findUsers(query: FilteredDataQuery<string>, bit: SystemRoleBit): Observable<UserListItem[]> {
    return this.userRestService.findAllAsDataSource({
      name: query.filter,
      showInactive: false,
      autocomplete: true,
      systemRoles: systemRolesBitSet(bit),
    }).select(query).pipe(map$(getSlicedData));
  }

  buildFinalVersion(letter: Letter, contactId: ID): Observable<Letter> {
    return this.letterRest.buildFinalVersion({
      contactId,
      jobId: this.jobId,
      filledTemplate: letter,
    });
  }

  deleteLetter(letter: Letter): Observable<void> {
    return this.letterRest.remove(letter);
  }

  initView(route: ActivatedRouteSnapshot): any {
    const jobId = getRouteParam(route, 'jobId');
    assign(this, { jobId });

    return {
      basicInfo: this.getBasicInfo(),
      reportUrl: this.report.generateUrl(REPORT_JOB_DETAILS, [reportIdField('Job', jobId)]),
    };
  }

  initGeneralTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      generalInfo: this.getGeneralTabInfo(),
      statusDict: this.dictionaryService.getByEnum(SalesFolderStatus),
      contractTypes: this.dictionaryService.getByEnum(ContractType),
      notProceedingReasons: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.notProceedingReasons)
        .pipe(map$((items) =>  items ? items.map(dictionaryItemFromDto) : [])),
    };
  }

  initDatesTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      datesInfo: this.getDatesInfo(),
    };
  }

  initRfqTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      rfq: this.getRfqTab(),
    };
  }

  initWarrantyTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      warrantyList: this.getWarrantyTaskList({ outstandingOnly: false }),
      gridLayout: this.gridLayoutService.get('jobsWarranty'),
    };
  }

  initLettersDocsTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      letterList: this.getLetters(),
      gridLayout: this.gridLayoutService.get('jobsLettersDocs'),
    };
  }

  initChecklistsTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      checklists: this.getChecklists(),
      gridLayout: this.gridLayoutService.get('jobsChecklists'),
    };
  }

  initEdocsTabView(route: ActivatedRouteSnapshot): any {
    const jobId = getParentJobId(route);
    return {
      jobId,
      ownerSet: this.jobEDocRest.getOwners(jobId),
      log: this.getEDocLog(jobId),
      signingStatusDict: this.dictionaryService.getByEnum(EDocSigningStatus),
      edocTypeDict: this.dictionaryService.getByEnum(EDocType),
    };
  }

  initSharedDocTabView(route: ActivatedRouteSnapshot): any {
    const jobId = getParentJobId(route);
    return {
      jobId,
      documents: this.getSharedDocuments(jobId),
      sharedDocsTypeDict: this.dictionaryService.getByEnum(JobSharedDocType),
    };
  }

  initVariationListTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      variationData: this.getVariationList(),
      gridLayout: this.gridLayoutService.get('jobDetailsChangeOrders'),
      totalGridLayout: this.gridLayoutService.get('jobDetailsChangeOrdersTotal'),
    };
  }

  initDrawsTabView(route: ActivatedRouteSnapshot): any {
    const jobId = getParentJobId(route);
    this.jobId = jobId;
    return {
      jobId,
      draws: this.getDraws(jobId),
      invoiceGridLayout: this.gridLayoutService.get('jobDetailsDrawsInvoices'),
      paymentGridLayout: this.gridLayoutService.get('jobDetailsDrawsPayments'),
      paymentScheduleGridLayout: this.gridLayoutService.get('jobDetailsDrawsPaymentSchedule'),
      lendersScheduleIncludedGridLayout: this.gridLayoutService.get('jobDetailsDrawsLendersScheduleIncluded'),
      lendersScheduleOverheadsGridLayout: this.gridLayoutService.get('jobDetailsDrawsLendersScheduleOverheads'),
      invoiceTypeDict: this.dictionaryService.getByEnum(InvoiceType),
      reportUrl: this.report.generateUrl(REPORT_JOB_LENDER_S_SCHEDULE, [reportIdField('JobId', jobId)]),
    };
  }

  initCommunicationListTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      communicationData: this.getCommunicationData(),
      communicationTypes: this.dictionaryService.getByEnum(CommunicationType),
      gridLayout: this.gridLayoutService.get('jobsCommunication'),
    };
  }

  initWarrantyTaskDialogView(route: DialogRouteSnapshot<JobDetailsWarrantyTaskDialogConfig>): any {
    const { input: { taskId, basicInfo } } = route;

    return {
      jobId: basicInfo.jobId,
      basicInfo,
      task: isNil(taskId) ? newEmptyWarrantyTask(basicInfo.jobId) : this.getWarrantyTask(taskId),
    };
  }

  isSuperVisorAdministrator(userId: ID): Observable<boolean> {
    return this.userRestService.checkSystemRole(userId, Enum.toValue(UserSystemRole, UserSystemRole.JobAdmin)).pipe(
      map$((roleValue: UserAssignedRole) => roleValue.roleAssigned));
  }

  initInsuranceTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      insurance: this.getInsurance(),
    };
  }

  createWarrantyTask(task: JobWarrantyTask): Observable<JobWarrantyTask> {
    return this.jobWarrantyRestService.create({
      ...task,
      jobId: this.jobId,
    }).pipe(map$(jobWarrantyTaskFromDto));
  }

  changeSupervisor(supervisor: UserListItem): Observable<UserListItem> {
    return this.generalTabRestService.changeSupervisor(this.jobId, supervisor);
  }

  getBasicInfo(): Observable<JobBasicInfo> {
    return this.jobRest.getBasic(this.jobId);
  }

  getDatesInfo(): Observable<JobDatesTabInfo> {
    return this.jobDatesInfoRestService.get(this.jobId);
  }

  getLetters(): Observable<LetterListItem[]> {
    return this.lettersRestService.get(this.jobId).pipe(map$(letterListFromDto));
  }

  getChecklists(): Observable<JobChecklist> {
    return this.jobChecklistsRestService.get(this.jobId);
  }

  getInsurance(): Observable<JobInsurance> {
    return this.insuranceRestService.get(this.jobId);
  }

  getWarrantyTask(taskId: ID): Observable<JobWarrantyTask> {
    return this.jobWarrantyRestService.get(this.jobId, taskId);
  }

  getCommunicationData(): Observable<CommunicationData> {
    return this.communicationRestService.get(this.jobId);
  }

  getGeneralTabInfo(): Observable<JobGeneralTabInfo> {
    return this.generalTabRestService.get(this.jobId);
  }

  getRfqTab(): Observable<JobRfq> {
    return this.jobRfqRestService.get(this.jobId).pipe(map$(rfqFromDto));
  }

  getWarrantyTaskList(filter: JobWarrantyFilter): Observable<JobWarrantyTask[]> {
    return this.jobWarrantyRestService.getList(this.jobId, filter);
  }

  getVariationList(): Observable<JobVariationResponse> {
    return this.jobChangeOrderRestService.findAllForTab(this.jobId);
  }

  getCommunicationDetails(id: ID): Observable<CommunicationListItem> {
    return this.communicationService.get(id);
  }

  getContacts(
    query: FilteredDataQuery<string>,
    types: ContactType[] = [ContactType.Employee, ContactType.CreditorSupplier],
  ): Observable<AutocompleteItem[]> {
    return this.contactRestService.findAllAsDataSource({
      types,
      autocomplete: true,
      showInactive: false,
      name: query.filter,
    }).select(query).pipe(
      map$(getSlicedData));
  }

  getUsers(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<UserAutocompleteDto[]> {
    return this.userRestService.getUserList(query);
  }

  getContracturalCompletion(data: ContracturalCompletionRequest): Observable<ContracturalCompletionResponse> {
    return this.jobDatesInfoRestService.getContracturalCOmpletion(this.jobId, data);
  }

  findCustomersByName(query: FilteredDataQuery<string>): Observable<JobCustomer[]> {
    return this.contactRestService.findAllCustomersAsDataSource({
      name: query.filter,
      showLeads: true,
    }).select(query).pipe(map$(getSlicedData));
  }

  completeWarrantyTask(job: JobWarrantyTask): Observable<JobWarrantyTask> {
    return this.jobWarrantyRestService.complete(job);
  }

  produceLetter(entryPoint: PrintEntryPoint, letter: LetterListItem): Observable<Letter> {
    return this.lettersRestService.produce(this.jobId, {
      jobDocName: letter.fileName,
      entryPoint,
    });
  }

  deleteWarrantyTask(job: JobWarrantyTask): Observable<void> {
    return this.jobWarrantyRestService.delete(job);
  }

  updateRfqTab(rfq: JobRfq): Observable<JobRfq> {
    return this.jobRfqRestService.update(this.jobId, rfq).pipe(map$(rfqFromDto));
  }

  updateInsurance(insurance: JobInsurance): Observable<JobInsurance> {
    return this.insuranceRestService.update(this.jobId, insurance);
  }

  sendEmail(email: CommunicationListItem): Observable<CommunicationListItem> {
    return this.communicationRestService.sendEmail(this.jobId, email);
  }

  updateWarrantyTask(task: JobWarrantyTask): Observable<JobWarrantyTask> {
    return this.jobWarrantyRestService.update({
      ...task,
      jobId: this.jobId,
    }).pipe(map$(jobWarrantyTaskFromDto));
  }

  updateDatesTabInfo(info: JobDatesTabInfo): Observable<JobDatesSave> {
    return this.jobDatesInfoRestService.update(this.jobId, info).pipe(
      // reload basic info if job was finalized
      switchMap$((datesInfo) => this.getBasicInfo().pipe(map$((basicInfo) => ({ basicInfo, datesInfo })))));
  }

  updateGeneralTabInfo(info: JobGeneralTabInfo): Observable<JobGeneralTabInfo> {
    return this.generalTabRestService.update(this.jobId, jobGeneralInfoToDto(info));
  }

  unfinalize(jobId: ID, version: EntityVersion): Observable<JobBasicInfo> {
    return this.jobRest.unfinalize(jobId, version);
  }

  updateEdocOwnerSet(jobId: ID, ownerSet: JobEDocOwnerSet): Observable<JobEDocOwnerSet> {
    return this.jobEDocRest.updateOwnerSet(jobId, ownerSet);
  }

  sendDocumentsToSign(jobId: ID, signingRequest: JobEDocSigningRequest): Observable<void> {
    return this.jobEDocRest.sendToSign(jobId, signingRequest);
  }

  getEDocLog(jobId: ID): Observable<JobEDocLog> {
    return this.jobEDocRest.getLog(jobId);
  }

  getDraws(jobId: ID): Observable<JobDrawsDetails> {
    return this.jobDrawsRest.getMaster(jobId).pipe(map$(jobDrawsDetailsFromDto));
  }

  recalculateDraws(jobId: ID, request: JobDrawsRecalculateRequest, newDrawRuleId?: ID): Observable<JobDrawsDetails> {
    return this.jobDrawsRest.recalculateMaster(jobId, request, newDrawRuleId).pipe(map$(jobDrawsDetailsFromDto));
  }

  refreshDraws(jobId: ID,
               lendersScheduleDrawRuleId: ID,
               request: JobDrawsRecalculateRequest): Observable<JobDrawsDetails> {
    return this.jobDrawsRest.refreshMaster(jobId,
                                           lendersScheduleDrawRuleId,
                                           request).pipe(map$(jobDrawsDetailsFromDto));
  }

  getSharedDocuments(jobId: ID): Observable<JobSharedDocListItem[]> {
    return this.jobSharedDocRest.findAllByJobId(jobId);
  }

  shareDocuments(jobId: ID, set: JobSharedDocSet): Observable<void> {
    return this.jobSharedDocRest.share(jobId, jobSharedDocSetToDto(set));
  }

  unshareDocument(jobId: ID, sharedDocId: ID): Observable<void> {
    return this.jobSharedDocRest.unshare(jobId, sharedDocId);
  }

  changeClient(jobId: ID, version: EntityVersion, contactId: ID, originalContactId: ID): Observable<JobBasicInfo> {
    return this.jobRest.changeClient(jobId, {
      version,
      contactId,
      originalContactId,
    });
  }

  readSms(id: ID): Observable<any> {
    return this.communicationService.setSmsAsRead(id);
  }

  findSalesPersonsByName(query: FilteredDataQuery<string>, excludeId?: ID): Observable<JobSalesPerson[]> {
    const requestObj = {
      name: query.filter,
      showInactive: false,
      autocomplete: true,
      systemRoles: systemRolesBitSet(SystemRoleBit.SalesPerson),
    } as any;
    if (excludeId) {
       requestObj.excludeIds = [excludeId];
    }
    return this.userRestService.findAllAsDataSource(requestObj).select(query).pipe(map$(getSlicedData));
  }

  reassign(jobId: ID, version: EntityVersion, salesPersonId: ID): Observable<void> {
    return this.jobRest.reassign(jobId, version, salesPersonId);
  }

  produceChecklist(entryPoint: PrintEntryPoint, checklist: JobChecklistItem): Observable<JobChecklistProduceDto> {
    return this.jobChecklistsRestService.generateImage(this.jobId, checklist.id)
      .pipe(switchMap$(() => this.jobChecklistsRestService.produce(this.jobId, checklist.id)));
  }

}
